import type { DocumentPagesParams } from "@common/api/documents"
import type {
  AirworthinessDirectiveFilters,
  ArchetypeName,
  DocumentFilters,
} from "@common/client"
import { AirworthinessDirectiveFiltersSchema } from "@common/client/schemas.gen"
import { z } from "zod"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  SORT_KEYS,
  withNumber,
} from "./columns"
import { type Grouping, groupingSchema } from "./grouping"

const DIRECTIVE_ARCHETYPES: ArchetypeName[] = ["airworthiness_directive"]

export const DIRECTIVE_FILTERS: DocumentFilters = {
  attributes: { archetype: DIRECTIVE_ARCHETYPES },
  superseded: true,
}

export const TABS = ["current", "all", "emergency", "faa", "easa"] as const

const tabSchema = z.enum(TABS)

const directionSchema = z.enum(["asc", "desc"])

export type Direction = z.infer<typeof directionSchema>

export type Tab = z.infer<typeof tabSchema>

export const regulatorSchema = z.enum(
  AirworthinessDirectiveFiltersSchema.properties.issued_by.items.enum,
)

export type Regulator = z.infer<typeof regulatorSchema>

export const directivesSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  issued_by: z.array(regulatorSchema).optional().catch(undefined),
  source_feed: z.array(regulatorSchema).optional().catch(undefined),
  manufacturer: z.array(z.string()).optional().catch(undefined),
  ata_chapter: z.array(z.number()).optional().catch(undefined),
  group: groupingSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: columnSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
})

export type DirectivesSearch = z.infer<typeof directivesSearchSchema>

export type DirectivesDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: Column
  direction: Direction
}

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "current":
      return "Current"
    case "all":
      return "All"
    case "emergency":
      return "Emergency"
    case "faa":
      return "FAA"
    case "easa":
      return "EASA"
  }
}

export function feedOf(tab: Tab): Regulator | undefined {
  switch (tab) {
    case "faa":
      return "FAA"
    case "easa":
      return "EASA"
    default:
      return undefined
  }
}

export function displayOf(search: DirectivesSearch): DirectivesDisplay {
  return {
    group: search.group,
    subgroup:
      search.group && search.subgroup !== search.group
        ? search.subgroup
        : undefined,
    columns: search.columns ? withNumber(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "added",
    direction: search.direction ?? (search.sort ? "asc" : "desc"),
  }
}

function directiveFilters(
  issuedBy: Regulator[],
  emergency: boolean,
): AirworthinessDirectiveFilters | null {
  if (issuedBy.length === 0 && !emergency) return null
  return { issued_by: issuedBy, emergency: emergency || null }
}

export function directivePagesParams(
  search: DirectivesSearch,
): DocumentPagesParams {
  const tab = search.tab ?? "current"
  const feed = feedOf(tab)
  const display = displayOf(search)
  return {
    search: search.search || null,
    superseded: tab === "all",
    airworthiness_directive: directiveFilters(
      search.issued_by ?? [],
      tab === "emergency",
    ),
    attributes: {
      archetype: DIRECTIVE_ARCHETYPES,
      regulator: feed ? [feed] : (search.source_feed ?? []),
      manufacturer: search.manufacturer ?? [],
      ata_chapter: search.ata_chapter ?? [],
    },
    order_by: SORT_KEYS[display.sort] ?? "created_at",
    direction: display.direction,
    group: display.group ?? null,
    subgroup: display.subgroup ?? null,
  }
}
